/** Admin API istemcisi: Bearer oturum jetonu sessionStorage'da (sekme kapanınca gider). */
const KEY = 'alem101.admin.token';
export const getToken = () => sessionStorage.getItem(KEY);
export const setToken = (t: string | null) =>
  t ? sessionStorage.setItem(KEY, t) : sessionStorage.removeItem(KEY);

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Oyuncu listesi süzgeci (arama ve Excel raporu aynı alanları kullanır). */
export type OyuncuSuzgeci = {
  status?: string;
  vip?: string;
  kind?: string;
  activeDays?: string;
};

export type OyuncuOzeti = {
  toplam: number;
  bot: number;
  etkin: number;
  dondurulmus: number;
  yasakli: number;
  silinmis: number;
  vip: number;
  gold: number;
  iletisimDogrulu: number;
  yeniBugun: number;
  yeni7: number;
  yeni30: number;
  aktif1: number;
  aktif7: number;
  aktif30: number;
  serverTime: string;
};

export type RaporTuru = 'oyuncular' | 'gunluk' | 'satin-almalar' | 'cip';

/**
 * Excel raporu indir (docs/66): oturum jetonu başlıkta gittiği için bağlantı
 * değil fetch + Blob; dosya adı sunucudan.
 */
export async function raporIndir(tur: RaporTuru, sorgu: Record<string, string> = {}) {
  const temiz = Object.fromEntries(Object.entries(sorgu).filter(([, v]) => v));
  const res = await fetch(`/admin/v1/raporlar/${tur}?${new URLSearchParams(temiz)}`, {
    headers: getToken() ? { authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) {
    if (res.status === 401) setToken(null);
    const data = (await res.json().catch(() => ({}))) as {
      error?: { code: string; message: string };
    };
    throw new ApiError(
      data.error?.code ?? `HTTP_${res.status}`,
      data.error?.message ?? 'rapor indirilemedi',
      res.status,
    );
  }
  const ad =
    /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
    `panpa-${tur}.xlsx`;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ad;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { ad, boyut: blob.size };
}

export async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const started = Date.now();
  const res = await fetch(path, {
    method,
    headers: {
      // Gövdesiz POST'ta content-type gönderilmez: Fastify boş JSON gövdeyi hata sayar
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(getToken() ? { authorization: `Bearer ${getToken()}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    error?: { code: string; message: string; details?: unknown };
  } & T;
  // Yerel kabul ölçümü: kimlik, sorgu, gövde ve token günlüğe girmez. Üretimde kaldırılır.
  if (import.meta.env.DEV)
    // eslint-disable-next-line no-console -- Yalnız yerel kabul ölçümü; üretim derlemesinde kaldırılır.
    console.debug(
      `[panel-request] ${method} ${path.split('?')[0]?.split('/')[3]} ${res.status} ${Date.now() - started}ms`,
    );
  if (!res.ok) {
    if (res.status === 401) setToken(null);
    throw new ApiError(
      data.error?.code ?? `HTTP_${res.status}`,
      data.error?.message ?? 'istek başarısız',
      res.status,
      data.error?.details,
    );
  }
  return data;
}

export type StaffMember = {
  id: string;
  email: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MODERATOR' | 'SUPPORT' | 'FINANCE';
  status: 'active' | 'disabled';
  createdAt: string;
  lastLoginAt: string | null;
};
export type BanInput = {
  kind: 'account' | 'device' | 'ip';
  value: string;
  hours: number | null;
  reason: string;
  device?: boolean;
  ip?: boolean;
};
export type BanRecord = {
  id: string;
  kind: BanInput['kind'];
  value: string;
  userId: string | null;
  reason: string;
  by: string;
  until: string | null;
  createdAt: string;
  liftedAt: string | null;
  liftedBy: string | null;
};
export type GameEntry = { id: string; name: string; status: 'ready' | 'soon' | 'maintenance' };
export type GamesResponse = {
  list: GameEntry[];
  defaults: GameEntry[];
  stored: boolean;
  updatedAt: number | null;
};
export type MetricSample = {
  at: number;
  src: string;
  rssMb: number;
  cpuPct: number;
  lagP99Ms: number;
  uptimeS: number;
  extra?: Record<string, number>;
};
export type MetricsResponse = {
  serverTime: number;
  sources: Array<{ src: string; up: boolean; now: MetricSample | null; series: MetricSample[] }>;
};
/** Cihaz performans özeti satırı (docs/65, `GET /admin/v1/perf`). */
export type PerfGroup = {
  platform: string;
  deviceModel: string;
  manufacturer: string | null;
  soc: string | null;
  ramMb: number | null;
  refreshHz: number | null;
  osVersion: string | null;
  impellerShare: number | null;
  appVersions: string[];
  screen: string;
  sessions: number;
  minutes: number;
  frames: number;
  slowPct: number;
  frozen: number;
  build: { p50: number; p90: number; p99: number };
  raster: { p50: number; p90: number; p99: number };
  buildMaxMs: number;
  rasterMaxMs: number;
  thermalMax: number;
  rssMaxMb: number;
};

export const api = {
  perf: (days: number, platform = '') =>
    call<{ buckets: number[]; rows: number; groups: PerfGroup[] }>(
      'GET',
      `/admin/v1/perf?${new URLSearchParams({ days: String(days), ...(platform ? { platform } : {}) })}`,
    ),
  games: () => call<GamesResponse>('GET', '/admin/v1/games'),
  saveGames: (list: GameEntry[], ifUpdatedAt: number | null) =>
    call<Audited>('PUT', '/admin/v1/games', { list, ifUpdatedAt }),
  resetGames: (ifUpdatedAt: number | null) =>
    call<Audited>('POST', '/admin/v1/games/reset', { ifUpdatedAt }),
  systemMetrics: (points: number) =>
    call<MetricsResponse>('GET', `/admin/v1/system/metrics?points=${points}`),
  clearChatRoom: (id: string) =>
    call<Audited & { broadcastFailed: boolean }>(
      'POST',
      `/admin/v1/chat/rooms/${encodeURIComponent(id)}/clear`,
      { reason: 'Panelden ekran temizliği' },
    ),
  chipFlow: (days: number) => call<ChipFlow>('GET', `/admin/v1/finance/chip-flow?days=${days}`),
  finance: (query: { userId?: string; before?: string; status?: string }) =>
    call<FinanceReport>(
      'GET',
      '/admin/v1/finance?' +
        new URLSearchParams(
          Object.entries(query).filter(([, v]) => Boolean(v)) as [string, string][],
        ).toString(),
    ),
  invites: () =>
    call<{
      invites: {
        id: string;
        email: string;
        role: string;
        expiresAt: string;
        consumedAt: string | null;
        revokedAt: string | null;
      }[];
    }>('GET', '/admin/v1/staff/invites'),
  inviteStaff: (body: { email: string; role: string; reason: string }) =>
    call<Audited & { id: string; token: string; expiresAt: string }>(
      'POST',
      '/admin/v1/staff/invites',
      body,
    ),
  revokeInvite: (id: string, reason: string) =>
    call<Audited>('POST', `/admin/v1/staff/invites/${encodeURIComponent(id)}/revoke`, { reason }),
  inviteInfo: (token: string) =>
    call<{ email: string; role: string; expiresAt: string }>(
      'GET',
      `/admin/v1/invite/${encodeURIComponent(token)}`,
    ),
  startInvite: (token: string, password: string) =>
    call<{ challenge: string; otpauth: string; expiresIn: number }>(
      'POST',
      `/admin/v1/invite/${encodeURIComponent(token)}/start`,
      { password },
    ),
  finishInvite: (token: string, challenge: string, totpCode: string) =>
    call<Audited>('POST', `/admin/v1/invite/${encodeURIComponent(token)}/setup`, {
      challenge,
      totpCode,
    }),
  bans: (filter: { q: string; kind: string; status: string; cursor: string }) =>
    call<{ bans: BanRecord[]; nextCursor: string | null }>(
      'GET',
      `/admin/v1/bans?${new URLSearchParams(Object.fromEntries(Object.entries(filter).filter(([, value]) => value !== '')))}`,
    ),
  createBan: (body: BanInput) => call<Audited & { ids: string[] }>('POST', '/admin/v1/bans', body),
  liftBan: (id: string, reason: string) =>
    call<Audited>('POST', `/admin/v1/bans/${encodeURIComponent(id)}/lift`, { reason }),
  revokePlayerSessions: (id: string, reason: string) =>
    call<Audited & { revokedSessions: number }>(
      'POST',
      `/admin/v1/users/${encodeURIComponent(id)}/revoke-sessions`,
      { reason },
    ),
  staff: () => call<{ staff: StaffMember[] }>('GET', '/admin/v1/staff'),
  staffSessions: (id: string) =>
    call<{ sessions: { id: string; createdAt: string; expiresAt: string }[] }>(
      'GET',
      `/admin/v1/staff/${encodeURIComponent(id)}/sessions`,
    ),
  updateStaff: (
    id: string,
    body: {
      role: StaffMember['role'];
      status: StaffMember['status'];
      expectedRole: StaffMember['role'];
      expectedStatus: StaffMember['status'];
      reason: string;
    },
  ) =>
    call<Audited & { revokedSessions: number }>(
      'PATCH',
      `/admin/v1/staff/${encodeURIComponent(id)}`,
      body,
    ),
  revokeStaffSessions: (id: string, reason: string) =>
    call<Audited & { revokedSessions: number }>(
      'POST',
      `/admin/v1/staff/${encodeURIComponent(id)}/revoke-sessions`,
      { reason },
    ),
  roleDefs: () =>
    call<{
      caps: string[];
      /** Kişi bazında verilen yüksek yetkiler (rol tanımına konamaz, docs/36 §0.1-3). */
      highCaps?: string[];
      capLabels?: Record<string, string>;
      defs: RoleDefinition[];
    }>('GET', '/admin/v1/role-defs'),
  saveRoleDef: (id: string, body: RoleDefinitionInput) =>
    call<Audited>('PUT', `/admin/v1/role-defs/${encodeURIComponent(id)}`, body),
  deactivateRole: (id: string, reason: string) =>
    call<Audited>('DELETE', `/admin/v1/role-defs/${encodeURIComponent(id)}`, { reason }),
  roleHolders: (role = '', scope = '') =>
    call<{ holders: RoleGrant[] }>(
      'GET',
      `/admin/v1/roles?${new URLSearchParams({ ...(role ? { role } : {}), ...(scope ? { scope } : {}) })}`,
    ),
  userRoles: (id: string) =>
    call<{ roles: RoleGrant[] }>('GET', `/admin/v1/users/${encodeURIComponent(id)}/roles`),
  grantRole: (
    id: string,
    body: {
      role: string;
      scope?: string;
      expiresAt?: number;
      caps?: string[] | null;
      reason: string;
    },
  ) => call<Audited>('POST', `/admin/v1/users/${encodeURIComponent(id)}/roles`, body),
  // Kişi bazında yetki listesi (docs/36 §0.1-2): null = rol deseni
  updateGrantCaps: (id: string, grantId: string, caps: string[] | null, reason: string) =>
    call<Audited & { caps: string[] | null }>(
      'PATCH',
      `/admin/v1/users/${encodeURIComponent(id)}/roles/${encodeURIComponent(grantId)}`,
      { caps, reason },
    ),
  // Giriş yöntemleri (docs/36 §0.1-4)
  userAuth: (id: string) =>
    call<UserAuthMethods>('GET', `/admin/v1/users/${encodeURIComponent(id)}/auth`),
  changeLoginEmail: (
    id: string,
    body: { email: string; reason: string; revokeSessions: boolean },
  ) =>
    call<Audited & { email: string; revokedSessions: number }>(
      'POST',
      `/admin/v1/users/${encodeURIComponent(id)}/auth/email`,
      body,
    ),
  setUserPassword: (
    id: string,
    body: { password: string; email?: string; reason: string; revokeSessions: boolean },
  ) =>
    call<Audited & { email: string | null; created: boolean; revokedSessions: number }>(
      'POST',
      `/admin/v1/users/${encodeURIComponent(id)}/auth/password`,
      body,
    ),
  sendPasswordResetCode: (id: string, reason: string) =>
    call<Audited & { email: string; ttlSeconds: number }>(
      'POST',
      `/admin/v1/users/${encodeURIComponent(id)}/auth/reset-code`,
      { reason },
    ),
  revokeRole: (id: string, grantId: string, reason: string) =>
    call<Audited>(
      'DELETE',
      `/admin/v1/users/${encodeURIComponent(id)}/roles/${encodeURIComponent(grantId)}`,
      { reason },
    ),
  maintenance: () => call<MaintenanceState>('GET', '/admin/v1/maintenance'),
  setMaintenance: (body: { active: boolean; message?: string; graceMinutes?: number }) =>
    call<Audited>('POST', '/admin/v1/maintenance', body),
  feedback: (
    status = 'open',
    kind = '',
    limit = 100,
    filters: { q?: string; before?: string } = {},
  ) =>
    call<{ open: number; items: FeedbackItem[]; nextCursor: string | null }>(
      'GET',
      `/admin/v1/support/feedback?${new URLSearchParams({ status, limit: String(limit), ...(kind ? { kind } : {}), ...filters })}`,
    ),
  updateFeedback: (id: string, body: { status: FeedbackItem['status']; note: string }) =>
    call<{ ok: true; auditId: string }>(
      'PATCH',
      `/admin/v1/feedback/${encodeURIComponent(id)}`,
      body,
    ),
  login: (email: string, password: string, totp: string) =>
    call<{ token: string; admin: { id: string; email: string; role: string } }>(
      'POST',
      '/admin/v1/auth/login',
      { email, password, totp },
    ),
  logout: () => call('POST', '/admin/v1/auth/logout'),
  me: () =>
    call<{
      admin: { adminId: string; email: string; role: string };
      /** Rol → eylem matrisi (sunucu: admin/capabilities.ts). Gizleme yetki değildir. */
      capabilities: string[];
    }>('GET', '/admin/v1/me'),
  overview: (days = 7) => call<Overview>('GET', `/admin/v1/overview?days=${days}`),
  stats: () => call<{ users: number; purchases: number }>('GET', '/admin/v1/stats'),
  users: (q: string, filters: OyuncuSuzgeci & { cursor?: string } = {}) =>
    call<{
      nextCursor?: string | null;
      users: Array<{
        userId: string;
        nickname: string;
        level: number;
        status: string;
        kind?: 'human' | 'bot';
        createdAt: string;
        lastSeenAt?: string | null;
        vipUntil: string | null;
        goldUntil?: string | null;
      }>;
    }>('GET', `/admin/v1/users?${new URLSearchParams({ q, ...filters })}`),
  /** Oyuncu Merkezi üst kartları (docs/66): gerçek oyuncular, botlar ayrı. */
  oyuncuOzeti: () => call<OyuncuOzeti>('GET', '/admin/v1/users/ozet'),
  user: (id: string) => call<UserDetail>('GET', `/admin/v1/users/${id}`),
  grantVip: (id: string, days: number, reason: string, tier: 'vip' | 'gold' = 'vip') =>
    call<Audited & { vipUntil: number; goldUntil?: number; cevrilenGun?: number }>(
      'POST',
      `/admin/v1/users/${id}/vip`,
      { days, reason, tier },
    ),
  setNickname: (id: string, nickname: string, reason: string) =>
    call<Audited & { nickname: string }>(
      'POST',
      `/admin/v1/users/${encodeURIComponent(id)}/nickname`,
      { nickname, reason },
    ),
  setNameColor: (id: string, color: string | null, reason: string) =>
    call<Audited & { nameColor: string | null }>('POST', `/admin/v1/users/${id}/name-color`, {
      color,
      reason,
    }),
  setStatus: (id: string, status: string, reason: string) =>
    call<{ ok: true; auditId: string }>('POST', `/admin/v1/users/${id}/status`, { status, reason }),
  adjust: (id: string, cls: string, amount: number, reason: string, idempotencyKey: string) =>
    call<{ txId: string; auditId: string; applied: boolean }>(
      'POST',
      `/admin/v1/users/${id}/adjust`,
      {
        idempotencyKey,
        class: cls,
        amount,
        reason,
      },
    ),
  salons: () => call<{ salons: Salon[] }>('GET', '/admin/v1/salons'),
  sendPush: (p: {
    title: string;
    body: string;
    route?: string;
    audience: 'all' | 'vip';
    collapseId?: string;
  }) => call<Audited & { jobId: string }>('POST', '/admin/v1/push', p),
  announcements: () => call<{ announcements: Announcement[] }>('GET', '/admin/v1/announcements'),
  createAnnouncement: (p: Partial<Announcement>) =>
    call<Audited & { id: string }>('POST', '/admin/v1/announcements', p),
  updateAnnouncement: (id: string, p: Partial<Announcement>) =>
    call<Audited>('PATCH', `/admin/v1/announcements/${id}`, p),
  deleteAnnouncement: (id: string) => call<Audited>('DELETE', `/admin/v1/announcements/${id}`),
  reports: (
    status: 'open' | 'dismissed' | 'actioned' | 'all' = 'open',
    limit = 100,
    filters: { q?: string; before?: string } = {},
  ) =>
    call<{ open: number; reports: Report[]; nextCursor: string | null }>(
      'GET',
      `/admin/v1/support/reports?${new URLSearchParams({ status, limit: String(limit), ...filters })}`,
    ),
  resolveReport: (id: string, status: 'dismissed' | 'actioned', note: string) =>
    call<Audited>('POST', `/admin/v1/reports/${id}/resolve`, { status, note }),
  userReports: (id: string) => call<{ reports: Report[] }>('GET', `/admin/v1/users/${id}/reports`),
  economy: () => call<Economy>('GET', '/admin/v1/economy'),
  runReconcile: () =>
    call<{
      ok: boolean;
      count: number;
      diffs: Array<{ userId: string; class: string; ledger: number; wallet: number }>;
    }>('POST', '/admin/v1/economy/reconcile'),
  investigate: (id: string, days = 30) =>
    call<Investigation>('GET', `/admin/v1/users/${id}/investigate?days=${days}`),
  matchDetail: (id: string) =>
    call<MatchDetail>('GET', `/admin/v1/matches/${encodeURIComponent(id)}`),
  userMatches: (id: string) =>
    call<{
      matches: Array<{
        matchId: string;
        seat: number;
        net: number | null;
        rank: number | null;
        at: string;
        salonId: string;
        bet: number;
        tableId: string;
      }>;
    }>('GET', `/admin/v1/users/${id}/matches`),
  appVersions: () =>
    call<{
      versions: Array<{
        platform: string;
        minVersion: string;
        latestVersion: string;
        maintenance: boolean;
        message: string | null;
      }>;
    }>('GET', '/admin/v1/app-versions'),
  setAppVersion: (
    platform: string,
    p: {
      minVersion?: string;
      latestVersion?: string;
      maintenance?: boolean;
      message?: string | null;
    },
  ) => call<Audited & Record<string, unknown>>('PUT', `/admin/v1/app-versions/${platform}`, p),
  defaultRules: (game = 'okey101') =>
    call<{
      rules: Record<string, unknown>;
      bilardo: { ruleTypes: string[]; raceTo: number[]; poolRaceTo: number[]; shotClock: number[] };
    }>('GET', `/admin/v1/rules/default?game=${encodeURIComponent(game)}`),
  /** Yeni salon kapalı açılır; `kopyala` aynı oyundaki salonun kurallarını alır. */
  createSalon: (p: {
    id: string;
    game: string;
    name: string;
    bet: number;
    minBalance: number;
    minLevel?: number;
    hero?: string | null;
    icon?: string | null;
    kopyala?: string | null;
  }) => call<{ auditId: string; salon: Salon }>('POST', '/admin/v1/salons', p),
  /** İçerik paketi dizini (herkese açık): görsel önizleme için. */
  contentManifest: () =>
    call<{ files: { path: string; sha256: string }[] }>('GET', '/content/manifest.json'),
  /** `ifUpdatedAt` verilirse koşullu kayıt: satır değiştiyse 409 CONFLICT + `details.current`. */
  updateSalon: (id: string, patch: Partial<Salon>, ifUpdatedAt?: number | null) =>
    call<SaveResult>('PATCH', `/admin/v1/salons/${id}`, {
      ...patch,
      ...(ifUpdatedAt === undefined ? {} : { ifUpdatedAt }),
    }),
  // --- mağaza katalogu ---
  storeProducts: () => call<{ products: StoreProduct[] }>('GET', '/admin/v1/store/products'),
  saveStoreProduct: (id: string, patch: Partial<StoreProduct>, ifRevision?: string | null) =>
    call<Audited>('PUT', `/admin/v1/store/products/${encodeURIComponent(id)}`, {
      ...patch,
      ifRevision,
    }),
  disableStoreProduct: (id: string) =>
    call<Audited>('DELETE', `/admin/v1/store/products/${encodeURIComponent(id)}`),
  // --- günlük bonus ---
  dailyTiers: () =>
    call<{ tiers: Array<{ day: number; amount: number; active: boolean }>; revision: string }>(
      'GET',
      '/admin/v1/daily-bonus',
    ),
  setDailyTiers: (tiers: Array<{ day: number; amount: number }>, ifRevision?: string) =>
    call<Audited>('PUT', '/admin/v1/daily-bonus', { tiers, ifRevision }),
  // --- çarkıfelek ---
  wheelPrizes: () => call<{ prizes: WheelPrize[] }>('GET', '/admin/v1/wheel/prizes'),
  wheelConfig: () =>
    call<{
      config: Array<{
        period: 'daily' | 'weekly';
        image: string;
        revision: string | null;
        slots: number;
        cooldownSeconds: number;
      }>;
    }>('GET', '/admin/v1/wheel/config'),
  setWheelConfig: (
    period: string,
    patch: { image?: string; slots?: number; cooldownSeconds?: number },
    ifRevision?: string | null,
  ) => call<Audited>('PUT', `/admin/v1/wheel/config/${period}`, { ...patch, ifRevision }),
  wheelStats: () =>
    call<{
      rows: Array<{ period: string; prizeId: string; spins: number; total: number }>;
    }>('GET', '/admin/v1/wheel/stats'),
  saveWheelPrize: (id: string, patch: Partial<WheelPrize>, ifRevision?: string | null) =>
    call<Audited>('PUT', `/admin/v1/wheel/prizes/${encodeURIComponent(id)}`, {
      ...patch,
      ifRevision,
    }),
  deleteWheelPrize: (id: string) =>
    call<Audited>('DELETE', `/admin/v1/wheel/prizes/${encodeURIComponent(id)}`),
  // --- sohbet odaları ---
  chatRooms: () =>
    call<{ rooms: ChatRoom[]; games: { id: string; name: string }[] }>(
      'GET',
      '/admin/v1/chat/rooms',
    ),
  saveChatRoom: (id: string, patch: Partial<ChatRoom>) =>
    call<Audited>('PUT', `/admin/v1/chat/rooms/${encodeURIComponent(id)}`, patch),
  closeChatRoom: (id: string) =>
    call<Audited>('DELETE', `/admin/v1/chat/rooms/${encodeURIComponent(id)}`),
  chatTail: (id: string) =>
    call<{
      messages: Array<{
        id: string;
        userId: string;
        nickname: string | null;
        body: string;
        hidden: boolean;
        createdAt: string;
      }>;
    }>('GET', `/admin/v1/chat/rooms/${encodeURIComponent(id)}/messages`),
  hideChatMessage: (id: string, hidden: boolean) =>
    call<Audited>('POST', `/admin/v1/chat/messages/${encodeURIComponent(id)}/hide`, { hidden }),
  flags: () =>
    call<{
      flags: Array<{ key: string; value: unknown; updatedBy: string | null; updatedAt: string }>;
    }>('GET', '/admin/v1/flags'),
  policies: () => call<{ policies: PolicyRow[] }>('GET', '/admin/v1/policies'),
  /** `ifUpdatedAt`: panelin gördüğü `updatedAt` (hiç kaydedilmemişse null) → iyimser kilit. */
  setFlag: (key: string, value: unknown, ifUpdatedAt?: number | null) =>
    call<SaveResult>('PUT', `/admin/v1/flags/${encodeURIComponent(key)}`, {
      value,
      ...(ifUpdatedAt === undefined ? {} : { ifUpdatedAt }),
    }),
  // --- canlı (docs/32) ---
  liveOverview: () => call<LiveOverview>('GET', '/admin/v1/live/overview'),
  liveMutes: () => call<{ mutes: LiveMute[] }>('GET', '/admin/v1/live/mutes'),
  liveEvents: (p: { after?: string; limit?: number; kinds?: string[] }) => {
    const q = new URLSearchParams();
    if (p.after) q.set('after', p.after);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.kinds && p.kinds.length) q.set('kinds', p.kinds.join(','));
    return call<{ events: OpsRecord[]; serverTime: number }>(
      'GET',
      `/admin/v1/live/events?${q.toString()}`,
    );
  },
  liveClose: (id: string, reason: string) =>
    call<Audited>('POST', `/admin/v1/live/tables/${encodeURIComponent(id)}/close`, { reason }),
  liveKick: (id: string, userId: string, reason: string) =>
    call<Audited>('POST', `/admin/v1/live/tables/${encodeURIComponent(id)}/kick`, {
      userId,
      reason,
    }),
  liveMute: (userId: string, minutes: number, reason: string) =>
    call<Audited & { until: number }>('POST', '/admin/v1/live/mute', { userId, minutes, reason }),
  liveUnmute: (userId: string) =>
    call<Audited>('DELETE', `/admin/v1/live/mute/${encodeURIComponent(userId)}`),
  /**
   * Denetim günlüğü: sunucu tarafı filtre + imleçli sayfalama. `q` denetim
   * kaydının id'si, hedef kimliği ya da aktör parçasıdır; `before` bir önceki
   * sayfanın `nextBefore` değeri. Parametresiz çağrı son 100 kaydı verir.
   */
  audit: (p: AuditQuery | number = 100) => {
    const q = typeof p === 'number' ? { limit: p } : p;
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(q))
      if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
    return call<{ entries: AuditEntry[]; nextBefore: string | null }>(
      'GET',
      `/admin/v1/audit?${s.toString()}`,
    );
  },
  auditEntry: (id: string) =>
    call<{ entry: AuditEntry }>('GET', `/admin/v1/audit/${encodeURIComponent(id)}`),
  auditActions: () => call<{ actions: string[] }>('GET', '/admin/v1/audit/actions'),
};

export interface Salon {
  game: string;
  id: string;
  name: string;
  gameMode: string;
  rules: Record<string, unknown>;
  bet: number;
  /** Masa açarken seçilebilen bahis basamakları (docs/20 D18). */
  betSteps: number[];
  minBalance: number;
  rakeBps: number;
  minLevel: number;
  vipOnly: boolean;
  /** Yalnız Gold VIP (docs/62). */
  goldOnly?: boolean;
  /** Kapasite (0 = sınırsız) ve doluyken VIP'e ek kontenjan (docs/62). */
  capacity?: number;
  vipExtraCapacity?: number;
  requiresUsta: boolean;
  botFill: boolean;
  active: boolean;
  sort: number;
  /** Kart anahtarı (içerik paketi: theme/v2/kart-salon-<hero>.webp); simge de boşsa buradan. */
  hero?: string | null;
  /** Simge anahtarı (theme/v2/ikon-salon-<icon>.webp); boş → kahramandan. */
  icon?: string | null;
  /** ISO zaman; koşullu kayıt için `Date.parse(updatedAt)` → `ifUpdatedAt`. */
  updatedAt: string;
}
export interface UserDetail {
  user: {
    id: string;
    status: string;
    trustTier: number;
    riskScore: number;
    riskReasons?: Array<{ code: string; points: number; detail?: string }>;
    createdAt: string;
    lastSeenAt: string | null;
  };
  profile: {
    nickname: string;
    level: number;
    xp: number;
    vipUntil: string | null;
    /** Gold VIP bitişi (docs/62). */
    goldUntil?: string | null;
    nameColor?: string | null;
  } | null;
  wallet: { PURCHASED: number; BONUS: number; GIFTED: number };
  ledger: Array<{
    id: string;
    class: string;
    amount: number;
    type: string;
    refType: string | null;
    refId: string | null;
    balanceAfter: number | null;
    createdAt: string;
  }>;
  sessions: Array<{ id: string; createdAt: string; lastUsedAt: string | null; ip: string | null }>;
  purchases: Array<{
    id: string;
    platform: string;
    productId: string;
    status: string;
    verifiedAt: string;
  }>;
}

export interface Announcement {
  id: string;
  kind: 'info' | 'event' | 'maintenance';
  title: string;
  body: string;
  ctaLabel: string | null;
  ctaRoute: string | null;
  startsAt: string | null;
  endsAt: string | null;
  active: boolean;
  sort: number;
  createdAt: string;
}

export interface StoreProduct {
  revision?: string;
  id: string;
  type: 'chips' | 'vip' | 'cosmetic';
  title: string;
  skuIos: string | null;
  skuAndroid: string | null;
  chipsAmount: number;
  vipDays: number;
  /** VIP günleri hangi kademeye (docs/62). */
  vipTier?: 'vip' | 'gold';
  priceTier: string | null;
  bonusPct: number;
  badge: string | null;
  active: boolean;
  sort: number;
}

export interface WheelPrize {
  revision?: string;
  id: string;
  period: 'daily' | 'weekly';
  slot: number;
  label: string;
  kind: 'chips' | 'vip' | 'none';
  amount: number;
  weight: number;
  active: boolean;
}

export interface ChatRoom {
  clearedAt: string | null;
  game: string | null;
  id: string;
  kind: 'global' | 'room';
  name: string;
  topic: string;
  minLevel: number;
  vipOnly: boolean;
  slowSeconds: number;
  active: boolean;
  sort: number;
}

// ---- canlı yönetim (docs/32) ----
export interface LiveNode {
  id: string;
  publicUrl: string;
  region: string;
  tables: number;
  players: number;
  startedAt: number;
  draining: boolean;
  updatedAt: number;
}
export interface LiveSeat {
  seat: number;
  userId: string | null;
  nickname: string | null;
  bot: boolean;
  ready: boolean;
  connected: boolean;
}
export interface LiveTable {
  game?: string;
  id: string;
  node: string;
  nodeUrl: string;
  salonId: string;
  bet: number;
  phase: string;
  isPrivate: boolean;
  rules: Record<string, unknown>;
  seats: LiveSeat[];
  updatedAt: number;
}
export interface LiveOverview {
  serverTime: number;
  nodes: LiveNode[];
  tables: LiveTable[];
  counts: { online: number; seated: number; tables: number; playing: number; waiting: number };
}
export interface LiveMute {
  userId: string;
  nickname: string | null;
  until: number;
  reason: string;
  by: string;
}
export interface AdminEvent {
  at: number;
  kind: string;
  seat?: number;
  userId?: string | null;
  text?: string;
  data?: Record<string, unknown>;
}
export interface TableMeld {
  id: string;
  owner: number;
  kind: string;
  slots: Array<{ tile: number; face: { color: string; value: number }; wild: boolean }>;
}
/** Oyun düğümünün admin kanalına bastığı kare (eller açık). */
export interface AdminTableView {
  id: string;
  removed?: true;
  node: string;
  salonId: string;
  bet: number;
  rules: Record<string, unknown>;
  isPrivate: boolean;
  botFill: boolean;
  phase: string;
  createdAt: number;
  matchNo: number;
  countdownEndsAt: number | null;
  deadline: number | null;
  serverTime: number;
  seats: Array<{
    seat: number;
    userId: string | null;
    nickname: string | null;
    bot: boolean;
    ready: boolean;
    connected: boolean;
    disconnectedAt: number | null;
    mutedUntil: number | null;
    hand: number[] | null;
  }>;
  public: null | {
    roundsPlayed: number;
    round: null | {
      roundNo: number;
      phase: string;
      gosterge: number;
      okey: { color: string; value: number };
      stackCount: number;
      discards: number[][];
      melds: TableMeld[];
      turn: { seat: number; step: string; turnNo: number };
      chain: number | null;
      pairsChain: number | null;
      scores: number[] | null;
    };
    players: Array<{
      seat: number;
      handCount: number;
      opened: string;
      openTotal: number;
      openPairs: number;
      penalties: Array<{ kind: string; points: number }>;
      left: boolean;
      bot: boolean;
      totalPenalty: number;
    }>;
  };
  recent: AdminEvent[];
}

/**
 * Masa izleme akışı (SSE, Bearer başlıklı fetch — EventSource başlık
 * taşıyamaz). Dönen fonksiyon akışı kapatır.
 */
export function streamTable(
  id: string,
  onFrame: (v: AdminTableView) => void,
  onError: (msg: string) => void,
): () => void {
  const ac = new AbortController();
  (async () => {
    const res = await fetch(`/admin/v1/live/tables/${encodeURIComponent(id)}/stream`, {
      headers: getToken() ? { authorization: `Bearer ${getToken()}` } : {},
      signal: ac.signal,
    });
    if (!res.ok || !res.body) {
      onError(`HTTP_${res.status}`);
      return;
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let i: number;
      while ((i = buf.indexOf('\n\n')) >= 0) {
        const chunk = buf.slice(0, i);
        buf = buf.slice(i + 2);
        let event = 'message';
        let data = '';
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data += line.slice(5).trim();
        }
        if (!data) continue;
        if (event === 'error') {
          const j = JSON.parse(data) as { code: string; message: string };
          onError(`${j.code}: ${j.message}`);
        } else onFrame(JSON.parse(data) as AdminTableView);
      }
    }
  })().catch((e) => {
    if (!ac.signal.aborted) onError(String(e));
  });
  return () => ac.abort();
}

/** Küresel olay şeridi kaydı (docs/32 §5). */
export interface OpsRecord {
  id: string;
  at: number;
  src: string;
  kind: string;
  level: 'info' | 'warn' | 'error';
  userId?: string | null;
  nickname: string | null;
  text?: string;
  data?: Record<string, unknown>;
}

// ---- soruşturma (docs/32 §5-2) ----
export interface Investigation {
  days: number;
  stored: {
    riskScore: number;
    trustTier: number;
    riskReasons: Array<{ code: string; points: number; detail?: string }>;
  };
  flags: Array<{ code: string; severity: 'low' | 'mid' | 'high'; detail: string }>;
  linked: {
    byIp: Array<{ userId: string; nickname: string | null; ips: string[] }>;
    byDevice: Array<{ userId: string; nickname: string | null; fingerprint: string }>;
  };
  devices: Array<{
    id: string;
    platform: string;
    model: string | null;
    appVersion: string | null;
    fingerprint: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
  }>;
  sessions: Array<{
    ip: string | null;
    deviceId: string | null;
    userAgent: string | null;
    createdAt: string;
    lastUsedAt: string | null;
    revoked: boolean;
  }>;
  counterparties: Array<{
    userId: string;
    nickname: string | null;
    games: number;
    myNet: number;
    theirNet: number;
    oneWay: boolean;
  }>;
  speed: {
    moves: number;
    medianMs: number | null;
    p10Ms: number | null;
    under400: number;
    suspicious: boolean;
  };
  transfers: {
    out: number;
    in: number;
    volumeOut: number;
    volumeIn: number;
    recent: Array<{
      id: string;
      direction: 'in' | 'out';
      other: string;
      otherNickname: string | null;
      amount: number;
      status: string;
      at: string;
    }>;
  };
  chat: Array<{ id: string; roomId: string; body: string; hidden: boolean; at: string }>;
}

export interface MatchDetail {
  match: {
    id: string;
    tableId: string;
    salonId: string;
    bet: number;
    rules: Record<string, unknown>;
    players: Array<{ seat: number; userId: string | null; nickname: string | null; bot: boolean }>;
    settlement: null | {
      pot: number;
      rake: number;
      winners: number[];
      payouts: Array<{ seat: number; rank: number; total: number; net: number }>;
    };
    startedAt: string;
    endedAt: string | null;
  };
  rounds: Array<{
    roundNo: number;
    seedHash: string;
    seed: string | null;
    starterSeat: number;
    okey: { color: string; value: number } | null;
    reason: string | null;
    finisherSeat: number | null;
    scores: number[] | null;
    totals: number[] | null;
    hands: number[][] | null;
    events: Array<Record<string, unknown> & { at?: number; type: string; seat?: number }>;
    startedAt: string;
    endedAt: string | null;
  }>;
}

// ---- ekonomi (docs/32 §5-3) ----
export interface Economy {
  at: number;
  circulation: { wallets: number; PURCHASED: number; BONUS: number; GIFTED: number; total: number };
  system: Record<string, { PURCHASED: number; BONUS: number; GIFTED: number; total: number }>;
  ledger: { entries: number; tx: number };
  holds: { active: number; total: number; stale: number; staleTotal: number };
  escrowOrphans: { refs: number; total: number; oldest: string | null };
  checks: {
    doubleEntry: { ok: boolean; sum: number };
    walletsMatchLedger: { ok: boolean; ledger: number; wallets: number };
    escrowMatchesHolds: { ok: boolean; escrow: number; holds: number };
  };
  reconcile: {
    lastMismatch: null | { at: string; after: unknown };
    lastManual: null | { at: string; actor: string; after: unknown };
    flags: Array<{ key: string; value: unknown; updatedBy: string | null }>;
  };
  daily: Array<{
    day: string;
    purchased: number;
    refunded: number;
    rewards: number;
    rake: number;
    burned: number;
    transferred: number;
    adjusted: number;
    tx: number;
  }>;
  rakeBySalon: Array<{ salonId: string; matches: number; pot: number; rake: number }>;
  top: Array<{
    userId: string;
    nickname: string | null;
    purchased: number;
    bonus: number;
    gifted: number;
    total: number;
  }>;
}

// ---- şikayetler ----
export interface Report {
  id: string;
  reporter: string;
  target: string;
  reporterNickname?: string | null;
  targetNickname?: string | null;
  targetOpenCount?: number;
  reason: 'cheating' | 'collusion' | 'abuse' | 'spam' | 'other';
  detail: string | null;
  tableId: string | null;
  matchId: string | null;
  roomId: string | null;
  messageId: string | null;
  /** Şikayet anındaki mesaj metni (mesaj saklama süresi sonunda silinse de kalır). */
  messageBody: string | null;
  status: 'open' | 'dismissed' | 'actioned';
  resolvedBy: string | null;
  resolvedAt: string | null;
  note: string | null;
  createdAt: string;
}

/** Panelden yönetilen politika (docs/34 §8): etkin değer + varsayılanlar. */
export interface PolicyRow {
  writeCapability: 'flag.write' | 'policy.write';
  key: string;
  help: string;
  value: Record<string, unknown>;
  defaults: Record<string, unknown>;
  stored: boolean;
  updatedAt: number | null;
}

/** Kritik kayıt yanıtı: denetim kaydının kimliği + yeni revizyon (Codex 037). */
export interface SaveResult {
  ok: true;
  auditId: string;
  updatedAt: number;
}

export interface AuditEntry {
  id: string;
  actor: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  before: unknown;
  after: unknown;
  ip?: string | null;
  createdAt: string;
}
export interface AuditQuery {
  /** Denetim kaydı id'si (tam), hedef kimliği (tam) ya da aktör parçası. */
  q?: string;
  action?: string;
  targetType?: string;
  /** ISO tarih ya da `Date.toISOString()`. */
  from?: string;
  to?: string;
  /** Önceki sayfanın `nextBefore` değeri. */
  before?: string;
  limit?: number;
}

/**
 * Denetim yazan her mutasyonun yanıtı (ekip/046): işlem kimliği. Panel bununla
 * başarı bildiriminden `GET /admin/v1/audit/:id` kaydına bağlantı verir.
 */
export interface Audited {
  ok?: true;
  auditId: string;
}

export type RoleDefinitionInput = {
  name: string;
  badge: string;
  color: string;
  scoped: boolean;
  caps: string[];
  muteMaxMinutes: number;
  rank: number;
  /** Eşit sıradakine de uygulanabilen yetkiler (docs/62 hiyerarşi). */
  peerCaps: string[];
  /** Kapsam türü (docs/62): global | salon | room | any. */
  scopeKind?: 'global' | 'salon' | 'room' | 'any';
  /** Yetki tavanları (docs/62); 0/boş = sınır yok. */
  limits?: {
    banMaxHours?: number;
    freezeMaxHours?: number;
    perDay?: Partial<Record<'mute' | 'kick' | 'ban' | 'freeze', number>>;
  };
  active: boolean;
};
export type RoleDefinition = RoleDefinitionInput & {
  id: string;
  holders: number;
  updatedAt: string;
};
export type RoleGrant = {
  id: string;
  userId: string;
  nickname?: string | null;
  role: string;
  scope: string | null;
  expiresAt: string | null;
  createdAt: string;
  grantedBy: string;
  reason?: string;
  revokedAt?: string | null;
  revokedBy?: string | null;
  revokeReason?: string | null;
  /** Kişi bazında yetkiler; null = rol deseni (docs/36 §0.1-2). */
  caps?: string[] | null;
};
export type UserAuthMethods = {
  identities: Array<{
    id: string;
    provider: 'guest' | 'apple' | 'google' | 'password' | string;
    email: string | null;
    hasEmail: boolean;
    hasPassword: boolean;
    emailVerifiedAt: string | null;
    linkedAt: string;
  }>;
  hasPassword: boolean;
  activeSessions: number;
  /** Çip/VIP verme yetkili hesap: giriş değişikliği yalnız üst yönetici. */
  staffLinked: boolean;
  canResetCode: boolean;
};
export type MaintenanceState = {
  active: boolean;
  message: string | null;
  until: number | null;
  since: number | null;
  openTables: number;
  players: number;
};
export type FeedbackItem = {
  id: string;
  userId: string;
  nickname: string | null;
  kind: 'bug' | 'idea' | 'other' | 'support';
  text: string;
  appVersion: string | null;
  platform: string | null;
  screen: string | null;
  status: 'open' | 'seen' | 'done';
  note: string | null;
  handledBy: string | null;
  handledAt: string | null;
  createdAt: string;
};

export interface FinanceReport {
  purchases: {
    id: string;
    userId: string;
    platform: string;
    productId: string;
    status: string;
    verifiedAt: string;
    refundedAt: string | null;
  }[];
  nextBefore: string | null;
  adjustments: {
    id: string;
    actor: string;
    targetId: string | null;
    before: unknown;
    after: unknown;
    createdAt: string;
  }[];
  emission: { dailyCap: number; alertPct: number; spent: number; day: string };
  account: {
    userId: string;
    nickname: string;
    vipUntil: string | null;
    purchased: number;
    bonus: number;
    gifted: number;
  } | null;
}

export interface PendingWork {
  kind: 'photos' | 'reports' | 'feedback';
  count: number;
  oldestAt: string | null;
}

export interface Overview {
  pendingWork: PendingWork[];
  tables: {
    open: number;
    players: number;
    byGame: { game: string; tables: number; players: number; bots: number; unknownSeats: number }[];
  } | null;
  feedbackPreview: { id: string; userId: string; text: string }[];
  serverTime: string;
  timeZone: 'Europe/Istanbul';
  users: number;
  openFeedback: number;
  maintenance: { active: boolean; message: string | null };
  online: { count: number; at: number } | null;
  daily: {
    date: string;
    registrations: number;
    active: number | null;
    state: 'measured' | 'unmeasured' | 'unavailable';
  }[];
  finance?: {
    purchasesByCurrentStatus: { status: string; count: number }[] | null;
    revenue: Revenue;
  };
}

export interface ChipFlow {
  revenue: Revenue;
  from: string;
  through: string;
  timeZone: 'Europe/Istanbul';
  serverTime: string;
  rows: {
    day: string;
    class: string;
    type: string;
    entries: number;
    incoming: string;
    outgoing: string;
    net: string;
  }[];
}

export interface Revenue {
  verified: RevenueRow[];
  refunded: RevenueRow[];
}
export interface RevenueRow {
  currency: string | null;
  amountMicros: string | null;
  count: number;
}
